import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, RouterLink } from '@angular/router';
import { filter, takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import {
  CotizadoresService,
  Cotizador,
} from '../../services/cotizadores.service';
import { MenuDataService, MenuItem } from '../../services/menu-data.service';
import { Subject } from 'rxjs';
import { AdminService } from '../../services/admin.service';
import { Subscription } from 'rxjs';
import { GoogleAnalyticsService } from '../../services/google-analytics/google-analytics.service';

/**
 * Componente que maneja la barra lateral de navegación
 * Gestiona el menú principal, submenús y menú informativo
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  // Variables para el manejo de los menús
  activeMenu: string | null = null;
  activeSubmenu: string | null = null;
  activeMenuInfo: string | null = null;
  isSidebarOpen = false;

  // Datos de los cotizadores
  cotizadores: Cotizador[] = [];
  menuItems: MenuItem[] = [];
  infomenuItems: MenuItem[] = [];

  private destroy$ = new Subject<void>();
  private subscription = new Subscription();

  /**
   * Constructor del componente
   * Inicializa los servicios necesarios y configura las suscripciones a eventos de navegación
   */
  constructor(
    private router: Router,
    private cotizadoresService: CotizadoresService,
    private menuDataService: MenuDataService,
    private adminService: AdminService,
    private gAnalytics:GoogleAnalyticsService
  ) {
    // Elementos iniciales del menú
    this.menuItems = this.menuDataService.getMenuItems();

    // Suscribirse a cambios en el menú
    this.menuDataService.menuItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.menuItems = this.menuDataService.getMenuItems();

        // Verificar la ruta actual y mantener el estado activo
        const currentUrl = this.router.url;

        // Primero buscar en submenús
        let foundInSubmenu = false;
        for (const item of this.menuItems) {
          if (item.subMenu) {
            const subItem = item.subMenu.find(
              (sub) => sub.route && `/inicio/${sub.route}` === currentUrl
            );
            if (subItem) {
              this.activeMenu = item.description;
              this.activeSubmenu = subItem.description;
              foundInSubmenu = true;
              break;
            }
          }
        }

        // Si no se encontró en submenús, buscar en menús principales
        if (!foundInSubmenu) {
          const menuItem = this.menuItems.find(
            (item) => item.route && `/inicio/${item.route}` === currentUrl
          );
          if (menuItem) {
            this.activeMenu = menuItem.description;
            this.activeSubmenu = null;
          }
        }
      });

    // Suscribirse a eventos de las rutas
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event: any) => {
        const currentUrl = this.router.url;

        // Buscar primero en los submenús
        let foundInSubmenu = false;

        for (const item of this.menuDataService.getMenuItems()) {
          if (item.subMenu) {
            for (const subItem of item.subMenu) {
              // Modificamos la lógica para manejar las rutas
              const routePath = subItem.route
                ? subItem.route.startsWith('/')
                  ? subItem.route
                  : `/inicio/${subItem.route}`
                : null;

              if (routePath && currentUrl === routePath) {
                this.activeMenu = item.description;
                this.activeSubmenu = subItem.description;
                foundInSubmenu = true;
                break;
              }
            }
            if (foundInSubmenu) break;
          }
        }

        // Si no se encontró en submenús, verificar rutas principales
        if (!foundInSubmenu) {
          if (currentUrl === '/inicio') {
            this.activeMenu = 'Principal';
            this.activeSubmenu = null;
          } else {
            // Verificar rutas directas
            for (const item of this.menuDataService.getMenuItems()) {
              if (item.route && currentUrl === item.route) {
                this.activeMenu = item.description;
                this.activeSubmenu = null;
                break;
              }
            }
          }
        }

        // Caso especial para cotizaciones
        if (currentUrl === '/inicio/cotizaciones') {
          this.activeMenu = 'Cotizadores';
          this.activeSubmenu = 'Cotizaciones';
        }

        // Verificar menú informativo
        for (const infoItem of this.menuDataService.getInfoMenuItems()) {
          if (infoItem.route && currentUrl === infoItem.route) {
            this.activeMenuInfo = infoItem.description;
            break;
          }
        }
      });

    // Cargar datos de cotizadores

    setTimeout(() => {
      this.loadCotizadoresData();
    }, 1000);
  }


  /**
   * Inicializa el componente
   * Configura suscripciones a cambios de rol y eventos de navegación
   */
  ngOnInit() {
    this.subscription.add(
      this.adminService.adminRole$.subscribe(() => {
        // Forzar actualización del menú
        this.menuDataService.refreshMenu();
        this.infomenuItems = this.menuDataService.getInfoMenuItems();
      })
    );

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        const currentUrl = this.router.url;
        let foundMatch = false;

        // Verificar menú informativo primero
        for (const item of this.menuDataService.getInfoMenuItems()) {
          if (item.route && currentUrl === item.route) {
            this.activeMenuInfo = item.description;
            this.activeMenu = null;
            this.activeSubmenu = null;
            foundMatch = true;
            break;
          }
        }

        // Si no se encontró en el menú informativo, verificar menú principal
        if (!foundMatch) {
          this.activeMenuInfo = null;

          // Verificar menú principal
          for (const item of this.menuDataService.getMenuItems()) {
            if (item.route && currentUrl === item.route) {
              this.activeMenu = item.description;
              this.activeSubmenu = null;
              foundMatch = true;
              break;
            }
            if (item.subMenu) {
              for (const subItem of item.subMenu) {
                const fullRoute = `/inicio/${subItem.route}`;
                if (currentUrl === fullRoute) {
                  this.activeMenu = item.description;
                  this.activeSubmenu = subItem.description;
                  foundMatch = true;
                  break;
                }
              }
              if (foundMatch) break;
            }
          }
        }

        // Caso especial para la ruta inicial
        if (currentUrl === '/inicio') {
          this.activeMenu = 'Principal';
          this.activeMenuInfo = null;
          this.activeSubmenu = null;
        }
      });
  }

  /**
   * Limpia las suscripciones al destruir el componente
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscription.unsubscribe();
  }

  /**
   * Maneja la navegación al hacer clic en elementos del menú
   * @param menuTitle Título del menú seleccionado
   * @param submenuName Nombre del submenú (opcional)
   */
  toggleSubmenu(menuTitle: string, submenuName?: string) {
    if(menuTitle === 'Cotizadores') this.updateCotizadoresSubmenu();

    // Para el menú principal
    const menuItem = this.menuDataService
      .getMenuItems()
      .find((item) => item.description === menuTitle);

    if (menuItem) {
      // Si tiene submenús con elementos y no se ha proporcionado un submenuName
      if (menuItem.subMenu && menuItem.subMenu.length > 0) {
        if (!submenuName) {
          // Solo alternar la expansión
          this.activeMenu = this.activeMenu === menuTitle ? null : menuTitle;
          this.activeMenuInfo = null;
        } else {
          // Click en un submenú
          const subItem = menuItem.subMenu.find(
            (sub) => sub.description === submenuName
          );
          if (subItem?.route) {
            this.router.navigate(['/inicio', subItem.route]);
            this.activeMenu = menuTitle;
            this.activeSubmenu = submenuName;
          }
        }
      } else {
        // Si no tiene submenús o tiene un array vacío y tiene ruta
        if (menuItem.route) {
          this.router.navigate(['/inicio', menuItem.route]);
          this.activeMenu = menuTitle;
          this.activeSubmenu = null;
          // Mantener el estado activo incluso después de la navegación
          this.menuDataService.menuItems$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
              this.activeMenu = menuTitle;
            });
        }
      }
    } else {
      // Para el menú informativo
      const infoItem = this.menuDataService
        .getInfoMenuItems()
        .find((item) => item.description === menuTitle);

      if (infoItem?.externalUrl) {
        window.open(infoItem.externalUrl, '_blank');
        this.activeMenuInfo = menuTitle;
        this.activeMenu = null;
        this.activeSubmenu = null;
      } else if (infoItem?.route) {
        this.router.navigate([infoItem.route]);
        this.activeMenuInfo = menuTitle;
        this.activeMenu = null;
        this.activeSubmenu = null;
      }
    }
  }

  /**
   * Alterna la visibilidad de la barra lateral
   */
  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  /**
   * Carga los datos de los cotizadores desde el servicio
   */
  loadCotizadoresData() {
    this.cotizadoresService.getCotizadores().subscribe({
      next: (cotizadores) => {
        this.cotizadores = cotizadores;
        this.updateCotizadoresSubmenu();
      },
      error: (error) => {
        console.error('Error al cargar cotizadores:', error);
      },
    });
  }

  /**
   * Actualiza el submenú de cotizadores con los datos obtenidos del servicio
   */
  private updateCotizadoresSubmenu() {
    const cotizadoresIndex = this.menuDataService
      .getMenuItems()
      .findIndex((item) => item.description === 'Cotizadores');

    if (cotizadoresIndex !== -1) {
      const menuItems = this.menuDataService.getMenuItems();

      // Mantenemos el submenú existente y solo actualizamos los cotizadores
      menuItems[cotizadoresIndex].subMenu = [
        ...(menuItems[cotizadoresIndex].subMenu?.map((item) => {
          // Si no es el item de 'Cotizaciones', buscamos el cotizador correspondiente
          if (item.description !== 'Cotizaciones') {
            const matchingCotizador = this.cotizadores.find(
              (c) =>
                c.description.toLowerCase() === item.description.toLowerCase()
            );

            if (matchingCotizador) {
              return {
                ...item,
                route: item.route || '', // Mantenemos la ruta existente
                cotizador: {
                  description: matchingCotizador.description,
                  url: matchingCotizador.url,
                },
              };
            }
          }
          return item;
        }) || []),
      ];

      // Actualizamos el menú
      this.menuItems = [...menuItems];
    }
  }

  /**
   * Abre un cotizador específico
   * @param cotizador Objeto cotizador a abrir
   */
  async abrirCotizador(cotizador: Cotizador) {
    try {
      await this.cotizadoresService.abrirCotizador(cotizador);

         //Evento para trackear las visitas a los distintos cotizadores desde el sidebar en Google Analytics
      this.gAnalytics.trackEvent('abrir_cotizador',{
        cotizador:cotizador.description
      })


    } catch (error) {
      console.error('Error al abrir cotizador:', error);
    }
  }
}
